import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { HeartPulse, X, ChevronLeft, BellOff } from 'lucide-react';
import api from '@/lib/api';
import StatusChip from '@/components/recovery/StatusChip';
import { fmtIST } from '@/components/recovery/recoveryUi';
import RecoveryDetail from './RecoveryDetail';
import useRecoveryLive from '@/components/recovery/useRecoveryLive';
import LiveDot from '@/components/recovery/LiveDot';

export default function RecoveryMonitorButton() {
  const [data, setData] = useState({ rows: [], urgent: 0 });
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const load = useCallback(async () => { try { const { data: d } = await api.get('/surgical/recovery/monitor'); setData(d); } catch { /* not entitled / offline */ } }, []);
  const [tick, setTick] = useState(0);
  const onChange = useCallback(() => { load(); setTick((n) => n + 1); }, [load]);
  const live = useRecoveryLive({ onChange });
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const dl = data.rows.map((r) => r.next_deadline && new Date(r.next_deadline).getTime()).filter(Boolean).sort((a, b) => a - b)[0];
    if (!dl) return undefined;
    const t = setTimeout(onChange, Math.max(1000, Math.min(dl - Date.now() + 1500, 2 ** 31 - 1)));
    return () => clearTimeout(t);
  }, [data.rows, onChange]);

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); load(); }}
              className="relative inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10" data-testid="surg-recovery-monitor">
        <HeartPulse size={12} /> Recovery Monitor
        {data.urgent > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center animate-pulse" data-testid="surg-recovery-urgent-badge">{data.urgent}</span>}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-3 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rec-monitor">
            <div className="px-5 py-3 flex items-center gap-3 rounded-t-2xl text-white" style={{ background: '#07122f' }}>
              {sel && <button type="button" onClick={() => { setSel(null); load(); }} data-testid="rec-monitor-back"><ChevronLeft size={18} /></button>}
              <LiveDot live={live} dark testId="rec-monitor-live-dot" />
              <div className="flex-1 text-[15px] font-black">Recovery Monitor {data.urgent > 0 && <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-red-600">{data.urgent} urgent</span>}</div>
              <button type="button" onClick={() => setOpen(false)} data-testid="rec-monitor-close"><X size={18} /></button>
            </div>
            <div className="p-4">
              {sel ? <RecoveryDetail planId={sel} refreshKey={tick} /> : (
                <ul className="divide-y divide-slate-100" data-testid="rec-monitor-list">
                  {data.rows.length === 0 && <li className="py-6 text-center text-[13px] text-slate-500">No published recovery plans yet.</li>}
                  {data.rows.map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => setSel(r.id)} className="w-full py-2.5 flex items-center gap-2 text-left hover:bg-slate-50 px-1 rounded" data-testid={`rec-monitor-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13.5px] font-bold truncate">{r.patient_name}</div>
                          <div className="text-[11.5px] text-slate-500 truncate">{r.operation} · Day {r.day} · last check-in {r.last_checkin_at ? fmtIST(r.last_checkin_at) : '—'}</div>
                        </div>
                        {!r.reminders_on && r.plan_status === 'published' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold" data-testid={`rec-monitor-reminders-off-${r.id}`}><BellOff size={10} /> Reminders OFF</span>}
                        <StatusChip status={r.status_chip} testId={`rec-monitor-status-${r.id}`} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
}
